import React, { useEffect, useMemo, useState } from "react";
import * as Icons from "../../data/teacher";
import { userRequest } from "../../utils/requestMethods";
import SkeletonCards from "../common/SkeletonCards";
import { Link } from "react-router-dom";
import {
  FiChevronDown,
  FiChevronUp,
  FiCheckCircle,
  FiXCircle,
  FiMoreVertical,
} from "react-icons/fi";

const SubjectsSection = ({
  teacher,
  school,
  handleShowUnassignSubjectModal,
  handleShowBulkUnassignModal,
  refreshKey = 0,
}) => {
  const [subjects, setSubjects] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [expandedPermissions, setExpandedPermissions] = useState({});
  const [openMenuId, setOpenMenuId] = useState(null);
  const [permissionsPopoverId, setPermissionsPopoverId] = useState(null);
  const [roleModalState, setRoleModalState] = useState({
    open: false,
    subjectData: null,
    classData: null,
    role: "",
  });
  const [permissionsModalState, setPermissionsModalState] = useState({
    open: false,
    subjectData: null,
    classData: null,
    permissions: [],
    search: "",
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [actionSuccess, setActionSuccess] = useState("");
  const [actionError, setActionError] = useState("");

  const fetchSubjects = async () => {
    setLoadingClasses(true);
    try {
      const { data } = await userRequest.get(
        `/teachers/teacher-assigned-subjects/${school?._id}/${teacher._id}?limit=50`,
      );
      console.log(data);
      setSubjects(data.subjects || data.assignments || []);
      setExpandedPermissions({}); // Reset permissions on fetch
    } catch (error) {
      console.error("Error fetching subjects:", error);
      setSubjects([]);
    } finally {
      setLoadingClasses(false);
    }
  };

  useEffect(() => {
    if (school?._id && teacher?._id) {
      fetchSubjects();
    }
  }, [school?._id, teacher._id, refreshKey]);

  useEffect(() => {
    const closeMenusOnOutsideClick = (event) => {
      if (!event.target.closest("[data-card-options]")) {
        setOpenMenuId(null);
      }
      if (!event.target.closest("[data-permissions-popover]")) {
        setPermissionsPopoverId(null);
      }
    };

    if (openMenuId !== null || permissionsPopoverId !== null) {
      document.addEventListener("mousedown", closeMenusOnOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", closeMenusOnOutsideClick);
    };
  }, [openMenuId, permissionsPopoverId]);

  const availableRoles = useMemo(
    () => [
      {
        value: "primary",
        label: "Primary",
        description: "Main teacher with full permissions and responsibilities",
      },
      {
        value: "assistant",
        label: "Assistant",
        description: "Teaching assistant with limited permissions",
      },
    ],
    [],
  );

  const availablePermissions = useMemo(
    () => [
      "view_students",
      "view_class_info",
      "view_tests",
      "view_questions",
      "view_results",
      "view_lesson_notes",
      "view_attendance",
      "create_tests",
      "edit_tests",
      "delete_tests",
      "create_questions",
      "edit_questions",
      "delete_questions",
      "create_results",
      "edit_results",
      "publish_results",
      "upload_lesson_notes",
      "edit_lesson_notes",
      "delete_lesson_notes",
      "mark_attendance",
      "edit_attendance",
      "edit_student_info",
      "edit_class_info",
    ],
    [],
  );

  const getRoleBadge = (role) => {
    const roleColors = {
      primary: "bg-blue-100 text-blue-800 border-blue-200",
      assistant: "bg-purple-100 text-purple-800 border-purple-200",
      coordinator: "bg-green-100 text-green-800 border-green-200",
    };

    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${
          roleColors[role] || "bg-gray-100 text-gray-800 border-gray-200"
        }`}
      >
        {role?.charAt(0).toUpperCase() + role?.slice(1) || "Teacher"}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    if (status === "active") {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <FiCheckCircle className="w-3 h-3 mr-1" />
          Active
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
        <FiXCircle className="w-3 h-3 mr-1" />
        Inactive
      </span>
    );
  };

  const formatPermission = (permission) => {
    return permission
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const togglePermissions = (assignmentId) => {
    setExpandedPermissions((prev) => ({
      ...prev,
      [assignmentId]: !prev[assignmentId],
    }));
  };

  const toggleMenu = (assignmentId) => {
    setOpenMenuId((prev) => (prev === assignmentId ? null : assignmentId));
  };

  const toErrorString = (payload) => {
    if (!payload) return "An unexpected error occurred.";
    if (typeof payload === "string") return payload;
    if (payload.message) return payload.message;
    if (payload.error) return payload.error;
    try {
      return JSON.stringify(payload);
    } catch {
      return "An unexpected error occurred.";
    }
  };

  const showSuccess = (message) => {
    setActionSuccess(message);
    setTimeout(() => setActionSuccess(""), 4000);
  };

  const showError = (message) => {
    setActionError(message);
    setTimeout(() => setActionError(""), 5000);
  };

  const openRoleModal = (subjectData, classData, currentRole) => {
    setRoleModalState({
      open: true,
      subjectData,
      classData,
      role: currentRole || availableRoles[0]?.value || "",
    });
    setOpenMenuId(null);
  };

  const closeRoleModal = () => {
    setRoleModalState({
      open: false,
      subjectData: null,
      classData: null,
      role: "",
    });
  };

  const openPermissionsModal = (subjectData, classData, currentPermissions = []) => {
    setPermissionsModalState({
      open: true,
      subjectData,
      classData,
      permissions: [...new Set(currentPermissions)],
      search: "",
    });
    setOpenMenuId(null);
  };

  const closePermissionsModal = () => {
    setPermissionsModalState({
      open: false,
      subjectData: null,
      classData: null,
      permissions: [],
      search: "",
    });
  };

  const handleRoleUpdate = async () => {
    if (!roleModalState.subjectData?._id || !roleModalState.classData?._id || !roleModalState.role)
      return;

    try {
      setActionLoading(true);
      setActionError("");

      await userRequest.patch("/teachers/update-teacher-role-in-subject", {
        schoolId: school?._id,
        teacherId: teacher?._id,
        classId: roleModalState.classData._id,
        subjectId: roleModalState.subjectData._id,
        newRole: roleModalState.role,
      });

      closeRoleModal();
      await fetchSubjects();
      showSuccess("Teacher role updated successfully.");
    } catch (error) {
      console.error("Error updating teacher role:", error);
      const payload = error?.response?.data;
      showError(toErrorString(payload));
    } finally {
      setActionLoading(false);
    }
  };

  const handlePermissionsUpdate = async () => {
    if (!permissionsModalState.subjectData?._id || !permissionsModalState.classData?._id) return;

    try {
      setActionLoading(true);
      setActionError("");

      await userRequest.patch("/teachers/update-teacher-permissions-in-subject", {
        schoolId: school?._id,
        teacherId: teacher?._id,
        classId: permissionsModalState.classData._id,
        subjectId: permissionsModalState.subjectData._id,
        newPermissions: permissionsModalState.permissions,
      });

      closePermissionsModal();
      await fetchSubjects();
      showSuccess("Teacher permissions updated successfully.");
    } catch (error) {
      console.error("Error updating teacher permissions:", error);
      const payload = error?.response?.data;
      showError(toErrorString(payload));
    } finally {
      setActionLoading(false);
    }
  };

  const filteredPermissions = useMemo(() => {
    const search = permissionsModalState.search.trim().toLowerCase();
    if (!search) return availablePermissions;

    return availablePermissions.filter((permission) => permission.toLowerCase().includes(search));
  }, [availablePermissions, permissionsModalState.search]);

  const handleSubjectSelection = (subjectId, classId) => {
    setSelectedSubjects((prev) => {
      const subjectKey = `${classId}_${subjectId}`;
      const isSelected = prev.some((item) => item.key === subjectKey);

      if (isSelected) {
        return prev.filter((item) => item.key !== subjectKey);
      } else {
        return [...prev, { key: subjectKey, subjectId, classId }];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedSubjects.length === subjects.length) {
      setSelectedSubjects([]);
    } else {
      const allSubjects = subjects.map((assignment) => {
        const subjectData = assignment.subject || assignment;
        const classData = assignment.class || subjectData.class || {};
        return {
          key: `${classData._id || assignment.classId || ""}_${subjectData._id || assignment.subjectId || assignment._id}`,
          subjectId: subjectData._id || assignment.subjectId || assignment._id,
          classId: classData._id || assignment.classId || "",
        };
      });
      setSelectedSubjects(allSubjects);
    }
  };

  const toggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode);
    setSelectedSubjects([]);
  };

  const handleBulkUnassign = () => {
    if (selectedSubjects.length > 0) {
      handleShowBulkUnassignModal(selectedSubjects);
    }
  };

  return (
    <div>
      {actionSuccess && (
        <div className="w-full mb-4 text-sm text-green-700 bg-green-100 border border-green-200 px-4 py-2 rounded-lg">
          {actionSuccess}
        </div>
      )}
      {actionError && (
        <div className="w-full mb-4 text-sm text-red-700 bg-red-100 border border-red-200 px-4 py-2 rounded-lg">
          {actionError}
        </div>
      )}
      {/* Selection Controls */}
      {subjects.length > 0 && (
        <div className="flex flex-wrap items-center justify-between mt-6 mb-4">
          <div className="flex items-center gap-3">
            <button
              onClick={toggleSelectionMode}
              className="bg-blue-100 text-blue-600 hover:bg-blue-200 font-medium text-sm px-4 py-2 rounded-full transition-colors float-right"
            >
              {isSelectionMode ? "Cancel Selection" : "Select Multiple"}
            </button>

            {isSelectionMode && (
              <>
                <button
                  onClick={handleSelectAll}
                  className="bg-gray-100 text-gray-600 hover:bg-gray-200 font-medium text-sm px-4 py-2 rounded-full transition-colors"
                >
                  {selectedSubjects.length === subjects.length ? "Deselect All" : "Select All"}
                </button>

                {selectedSubjects.length > 0 && (
                  <button
                    onClick={handleBulkUnassign}
                    className="bg-red-100 text-red-600 hover:bg-red-200 font-medium text-sm px-4 py-2 rounded-full transition-colors"
                  >
                    Unassign Selected ({selectedSubjects.length})
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center mt-6 mb-6">
        {loadingClasses ? (
          <SkeletonCards cardCount={3} layout="vertical" width="md:w-1/3 w-1/2" />
        ) : subjects.length > 0 ? (
          subjects.map((assignment, index) => {
            // Handle both new structure (assignment.subject, assignment.class) and legacy (direct subject data)
            const subjectData = assignment.subject || assignment;
            const classData = assignment.class || subjectData.class || {};
            const assignmentId = assignment.assignmentId || assignment._id || index;
            const role = assignment.teacher_role || assignment.role;
            const status = assignment.status;
            const permissions = assignment.teacher_permissions || assignment.permissions || [];

            const subjectKey = `${classData._id || assignment.classId || ""}_${subjectData._id || assignment.subjectId || assignment._id}`;
            const isSelected = selectedSubjects.some((item) => item.key === subjectKey);

            return (
              <div key={assignmentId} className="pr-3 md:w-1/3 w-1/2 mb-3">
                <div
                  className={`border p-3 rounded-2xl relative ${isSelected ? "border-blue-500 bg-blue-50" : "border-[#C5C5E8]"}`}
                  data-card-options
                >
                  {!isSelectionMode && (
                    <button
                      type="button"
                      onClick={() => toggleMenu(assignmentId)}
                      className="absolute top-3 right-3 text-slate-500 hover:text-slate-900 transition-colors bg-white rounded-full p-2 z-10"
                      aria-label="Subject options"
                    >
                      <FiMoreVertical className="w-5 h-5" />
                    </button>
                  )}
                  {openMenuId === assignmentId && (
                    <div className="absolute top-10 right-3 bg-white border border-slate-200 rounded-lg shadow-lg w-48 z-20">
                      <button
                        type="button"
                        onClick={() => openRoleModal(subjectData, classData, role)}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-slate-100 transition-colors"
                      >
                        Edit Teacher Role
                      </button>
                      <button
                        type="button"
                        onClick={() => openPermissionsModal(subjectData, classData, permissions)}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-slate-100 transition-colors border-t border-slate-100"
                      >
                        Edit Permissions
                      </button>
                    </div>
                  )}
                  {isSelectionMode && (
                    <div className="absolute top-3 right-3 z-10">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() =>
                          handleSubjectSelection(
                            subjectData._id || assignment.subjectId || assignment._id,
                            classData._id || assignment.classId || "",
                          )
                        }
                        className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                      />
                    </div>
                  )}
                  <div className="bg-[#EEEEFC] flex items-center justify-center rounded-2xl px-4 py-14">
                    <img src={Icons.book} alt="Nav Icon" />
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5 flex-wrap mt-3">
                      <p className="text-sm font-semibold text-slate-900">
                        {subjectData.subject_name || "Subject Name"}
                      </p>
                      {subjectData.subject_ref_id && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200 uppercase tracking-wider">
                          Shared
                        </span>
                      )}
                    </div>
                    <p className="text-xs mt-1 text-slate-500">
                      {classData.class_name || "Primary"}
                    </p>
                    {subjectData.lesson_note && (
                      <p className="text-xs mt-1">
                        {subjectData.lesson_note.length || 0} Lesson Notes
                      </p>
                    )}

                    {/* Role and Status */}
                    <div className="flex items-center gap-2 mt-2 mb-2">
                      {role && getRoleBadge(role)}
                      {status && getStatusBadge(status)}
                    </div>

                    {/* Permissions */}
                    {permissions && permissions.length > 0 && (
                      <div className="mt-2 mb-3 relative" data-permissions-popover>
                        <button
                          onClick={() => togglePermissions(assignmentId)}
                          className="flex items-center justify-between w-full text-xs font-medium text-slate-600 hover:text-slate-900 transition-colors"
                        >
                          <span>Permissions ({permissions.length}):</span>
                          {expandedPermissions[assignmentId] ? (
                            <FiChevronUp className="w-3 h-3" />
                          ) : (
                            <FiChevronDown className="w-3 h-3" />
                          )}
                        </button>
                        <div
                          className={`overflow-hidden transition-all duration-300 ease-in-out ${
                            expandedPermissions[assignmentId]
                              ? "max-h-20 opacity-100 mt-2"
                              : "max-h-0 opacity-0"
                          }`}
                        >
                          <div className="flex flex-wrap gap-1 relative">
                            {permissions.slice(0, 3).map((permission, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200"
                              >
                                {formatPermission(permission)}
                              </span>
                            ))}
                            {permissions.length > 3 && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPermissionsPopoverId(
                                    permissionsPopoverId === assignmentId ? null : assignmentId,
                                  );
                                }}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700 border border-indigo-200 hover:bg-indigo-200 transition-colors"
                              >
                                +{permissions.length - 3} more
                              </button>
                            )}
                          </div>
                        </div>
                        {permissionsPopoverId === assignmentId && (
                          <div className="absolute bottom-full left-0 right-0 mb-2 bg-white border border-slate-200 rounded-lg shadow-lg p-3 z-30 max-h-64 overflow-y-auto">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-semibold text-slate-700">
                                All Permissions ({permissions.length})
                              </span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPermissionsPopoverId(null);
                                }}
                                className="text-slate-400 hover:text-slate-600 text-xs"
                              >
                                ×
                              </button>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {permissions.map((permission, idx) => (
                                <span
                                  key={idx}
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200"
                                >
                                  {formatPermission(permission)}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center justify-between mt-3">
                    {!isSelectionMode && (
                      <button
                        onClick={() =>
                          handleShowUnassignSubjectModal(
                            classData._id || assignment.classId || "",
                            subjectData._id || assignment.subjectId || assignment._id,
                          )
                        }
                        className="sm:w-1/2 w-full bg-[#E26E6A1A] text-[#E26E6A] hover:bg-red-100 font-semibold text-xs sm:px-10 px-2 py-2 rounded-full flex items-center justify-center"
                      >
                        Unassign
                      </button>
                    )}
                    <div
                      className={`sm:pr-3 pr-0 ${!isSelectionMode ? "sm:w-1/2" : ""} w-full sm:mt-0 mt-2 sm:pl-2 pl-0 flex items-center justify-end`}
                    >
                      <Link
                        to={`/subjects/${classData._id || assignment.classId || ""}`}
                        className="w-full"
                      >
                        <button className="w-full bg-[#4543B5] hover:bg-indigo-900 text-white font-semibold text-xs sm:px-10 px-2 py-2 rounded-full flex items-center justify-center">
                          View
                        </button>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center w-full text-gray-500">
            No subjects assigned to this class yet.
          </div>
        )}
      </div>

      {/* Edit role modal */}
      {roleModalState.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30 backdrop-blur-sm px-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800">Update Teacher Role</h3>
              <button
                type="button"
                onClick={closeRoleModal}
                className="text-slate-500 hover:text-slate-900 transition-colors text-sm"
                disabled={actionLoading}
              >
                Close
              </button>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              Choose a new role for{" "}
              <span className="font-medium text-slate-700">
                {roleModalState.subjectData?.subject_name}
              </span>{" "}
              in{" "}
              <span className="font-medium text-slate-700">
                {roleModalState.classData?.class_name}
              </span>
              .
            </p>
            <div className="space-y-3">
              {availableRoles.map((optionRole) => (
                <label
                  key={optionRole.value}
                  className={`flex items-center justify-between border rounded-lg px-4 py-3 text-sm cursor-pointer transition-colors ${
                    roleModalState.role === optionRole.value
                      ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                      : "border-slate-200 hover:border-indigo-400"
                  }`}
                >
                  <div className="flex flex-col text-left">
                    <span className="font-medium">{optionRole.label}</span>
                    {optionRole.description && (
                      <span className="text-xs text-slate-500 mt-1">{optionRole.description}</span>
                    )}
                  </div>
                  <input
                    type="radio"
                    name="teacherRole"
                    value={optionRole.value}
                    checked={roleModalState.role === optionRole.value}
                    onChange={() =>
                      setRoleModalState((prev) => ({
                        ...prev,
                        role: optionRole.value,
                      }))
                    }
                    className="form-radio text-indigo-600"
                  />
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={closeRoleModal}
                className="px-4 py-2 text-sm rounded-full border border-slate-200 text-slate-600 hover:bg-slate-100 transition-colors"
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRoleUpdate}
                className="px-4 py-2 text-sm rounded-full bg-[#4543B5] text-white hover:bg-indigo-900 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={actionLoading}
              >
                {actionLoading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit permissions modal */}
      {permissionsModalState.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30 backdrop-blur-sm px-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800">Update Teacher Permissions</h3>
              <button
                type="button"
                onClick={closePermissionsModal}
                className="text-slate-500 hover:text-slate-900 transition-colors text-sm"
                disabled={actionLoading}
              >
                Close
              </button>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              Adjust permissions for{" "}
              <span className="font-medium text-slate-700">
                {permissionsModalState.subjectData?.subject_name}
              </span>{" "}
              in{" "}
              <span className="font-medium text-slate-700">
                {permissionsModalState.classData?.class_name}
              </span>
              .
            </p>
            <div className="mb-4">
              <input
                type="text"
                placeholder="Search permissions..."
                value={permissionsModalState.search}
                onChange={(event) =>
                  setPermissionsModalState((prev) => ({
                    ...prev,
                    search: event.target.value,
                  }))
                }
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
              />
            </div>
            <div className="flex-1 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pr-2">
                {filteredPermissions.map((permission) => (
                  <label
                    key={permission}
                    className="flex items-center gap-2 border border-slate-200 rounded-lg px-3 py-2 text-sm hover:border-indigo-400 transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={permissionsModalState.permissions.includes(permission)}
                      onChange={(event) => {
                        const checked = event.target.checked;
                        setPermissionsModalState((prev) => {
                          const updated = checked
                            ? [...prev.permissions, permission]
                            : prev.permissions.filter((item) => item !== permission);
                          return {
                            ...prev,
                            permissions: updated,
                          };
                        });
                      }}
                      className="form-checkbox text-indigo-600"
                    />
                    <span className="capitalize">{formatPermission(permission)}</span>
                  </label>
                ))}
              </div>
              {filteredPermissions.length === 0 && (
                <div className="text-center text-sm text-slate-500 mt-4">
                  No permissions match your search.
                </div>
              )}
            </div>
            <div className="flex justify-between items-center mt-6">
              <button
                type="button"
                onClick={() =>
                  setPermissionsModalState((prev) => ({
                    ...prev,
                    permissions: availablePermissions,
                  }))
                }
                className="text-sm text-indigo-600 hover:text-indigo-800 transition-colors"
                disabled={actionLoading}
              >
                Select all
              </button>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() =>
                    setPermissionsModalState((prev) => ({
                      ...prev,
                      permissions: [],
                    }))
                  }
                  className="px-4 py-2 text-sm rounded-full border border-slate-200 text-slate-600 hover:bg-slate-100 transition-colors"
                  disabled={actionLoading}
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={closePermissionsModal}
                  className="px-4 py-2 text-sm rounded-full border border-slate-200 text-slate-600 hover:bg-slate-100 transition-colors"
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handlePermissionsUpdate}
                  className="px-4 py-2 text-sm rounded-full bg-[#4543B5] text-white hover:bg-indigo-900 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  disabled={actionLoading}
                >
                  {actionLoading ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubjectsSection;
